import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useStore } from '@store/StoreProvider'
import { getLanguageMeta, normalizeLangCode } from '@utils/languageMeta'

function GlobeIcon() {
  return (
    <svg className="lang-globe" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="lang-item-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
    </svg>
  )
}

const FALLBACK_LANGUAGES = ['ru', 'en', 'ua', 'kz']

type LangSwitcherProps = {
  variant?: 'dropdown' | 'pills'
  className?: string
}

export const LangSwitcher = observer(function LangSwitcher({
  variant = 'dropdown',
  className = '',
}: LangSwitcherProps) {
  const { i18n } = useStore()
  const [isOpen, setIsOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const languages =
    i18n.availableLanguages.length > 0 ? i18n.availableLanguages : FALLBACK_LANGUAGES
  const currentLanguage = normalizeLangCode(i18n.language || 'ru')
  const tLang = (key: string) => i18n.t(key)
  const currentMeta = getLanguageMeta(currentLanguage, tLang)

  useEffect(() => {
    if (variant !== 'dropdown') return
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setIsOpen(false)
    }
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onEsc)
    }
  }, [variant])

  const select = (lang: string) => {
    const next = normalizeLangCode(lang)
    if (next && next !== currentLanguage) void i18n.setLanguage(next)
    setIsOpen(false)
  }

  if (variant === 'pills') {
    return (
      <div className={`flex items-center gap-2 flex-wrap ${className}`}>
        {languages.map((lang) => {
          const meta = getLanguageMeta(lang, tLang)
          const isActive = meta.code === currentLanguage
          return (
            <button
              key={meta.code}
              type="button"
              onClick={() => select(meta.code)}
              className={`lang-pill${isActive ? ' lang-pill--active' : ''}`}
            >
              <img
                src={`/assets/flags/${meta.flag}.svg`}
                alt=""
                className="w-5 h-4 object-cover rounded"
                style={{ height: '16px', width: 'auto', borderRadius: '50px' }}
              />
              <span className="lang-pill__code">{meta.short}</span>
              {meta.label}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className={`lang-wrap ${className}`}>
      <button
        className="lang-btn cursor-pointer"
        type="button"
        aria-label={i18n.t('nav.change_language')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        ref={btnRef}
      >
        <GlobeIcon />
        <span className="lang-code">{currentMeta.short}</span>
        <svg className="lang-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <div
        className={`lang-menu ${isOpen ? 'open' : ''}`}
        role="menu"
        aria-label={i18n.t('nav.change_language')}
        ref={menuRef}
      >
        {languages.map((lang) => {
          const meta = getLanguageMeta(lang, tLang)
          const isActive = meta.code === currentLanguage
          return (
            <button
              key={meta.code}
              className={`lang-item cursor-pointer ${isActive ? 'active' : ''}`}
              data-lang={meta.code}
              onClick={() => select(meta.code)}
              role="menuitem"
              aria-current={isActive ? 'true' : undefined}
            >
              <span className="inline-flex items-center gap-2">
                <img
                  src={`/assets/flags/${meta.flag}.svg`}
                  alt=""
                  className="w-5 h-4 object-cover rounded"
                  style={{ height: '16px', width: 'auto', borderRadius: '50px' }}
                />
                <span className="lang-item-short">{meta.short}</span>
              </span>
              <span className="lang-item-label">{meta.label}</span>
              {isActive ? <CheckIcon /> : <span className="lang-item-check" aria-hidden="true" />}
            </button>
          )
        })}
      </div>
    </div>
  )
})
